// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

/**
 * Reusable column formatting utilities for metric entities
 *
 * This utility provides standardized formatting functions for common column types
 * across all metric entities, ensuring consistency and reducing code duplication.
 */

import { Column, DataRow } from '@lfx/shared/interfaces';

/**
 * Date formatting options
 */
export enum DateFormat {
  /** "Jan 2024" */
  MONTH_YEAR = 'month_year',
  /** "Jan 15, 2024" */
  MONTH_DAY_YEAR = 'month_day_year',
  /** "Jan 15, 2024 14:30:00" */
  FULL_DATETIME = 'full_datetime'
}

/**
 * Platform types for username formatting
 */
export enum PlatformType {
  GITHUB = 'github',
  GITLAB = 'gitlab',
  GERRIT = 'gerrit',
  OTHER = 'other'
}

/**
 * Column formatter for user/member profile with avatar image
 *
 * Used in: Contributors, Maintainers, Commit Activities, Pull Requests Opened
 *
 * @param displayName - User's display name
 * @param avatarUrl - URL to user's avatar image
 * @param memberId - Optional member ID for extra data
 * @returns DataRow cell with HTML profile display
 */
export function formatUserProfile(
  displayName: string,
  avatarUrl: string,
  memberId?: string
): { text: string; isHtml: boolean; extraData?: any } {
  const imageHtml = avatarUrl
    ? `<img src="${avatarUrl}" alt="${displayName}" class="maintainer-avatar" onerror="this.style.display='none'">`
    : '';

  const result: { text: string; isHtml: boolean; extraData?: any } = {
    text: `<div class="maintainer-profile">${imageHtml}<span class="maintainer-name">${displayName}</span></div>`,
    isHtml: true
  };

  if (memberId) {
    result.extraData = {
      memberId,
      memberDisplayName: displayName,
      memberLogoUrl: avatarUrl
    };
  }

  return result;
}

/**
 * Column formatter for platform username with icon
 *
 * Used in: Contributors, Maintainers, Avg Time to Merge PRs
 *
 * @param platform - Platform name (github, gitlab, etc.)
 * @param username - Username on the platform
 * @returns DataRow cell with platform-specific formatting
 */
export function formatPlatformUsername(
  platform: string,
  username: string
): { text: string; isHtml?: boolean } {
  const platformLower = platform.toLowerCase();

  switch (platformLower) {
    case PlatformType.GITHUB:
      return {
        text: `<i class="fab fa-github" style="margin-right: 4px;"></i> &nbsp;@${username}`,
        isHtml: true
      };
    case PlatformType.GITLAB:
      return {
        text: `<i class="fab fa-gitlab" style="margin-right: 4px;"></i> &nbsp;@${username}`,
        isHtml: true
      };
    case PlatformType.GERRIT:
      return {
        text: `<i class="fas fa-code-branch" style="margin-right: 4px;"></i> &nbsp;${username}`,
        isHtml: true
      };
    default:
      return { text: username };
  }
}

/**
 * Column formatter for repository name extraction from URL
 *
 * Used in: Commit Activities, Pull Requests Opened, Avg Time to Merge PRs
 *
 * @param repositoryUrl - Full repository URL
 * @param format - 'simple' returns just repo name, 'org-repo' returns org/repo
 * @returns Repository name or org/repo format
 */
export function formatRepositoryName(
  repositoryUrl: string,
  format: 'simple' | 'org-repo' = 'org-repo'
): string {
  if (!repositoryUrl) return 'N/A';

  try {
    const parts = repositoryUrl.split('/');

    if (format === 'simple') {
      // Return just the last part (repo name)
      return parts[parts.length - 1] || repositoryUrl;
    } else {
      // Return org/repo format
      if (parts.length >= 2) {
        return `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
      }
      return repositoryUrl;
    }
  } catch {
    return repositoryUrl;
  }
}

/**
 * Column formatter for date/time display
 *
 * Used in: All metrics with various formats
 *
 * @param dateString - ISO date string
 * @param format - Desired output format
 * @returns Formatted date string
 */
export function formatDate(
  dateString: string,
  format: DateFormat = DateFormat.MONTH_DAY_YEAR
): string {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);

    switch (format) {
      case DateFormat.MONTH_YEAR:
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short'
        });

      case DateFormat.MONTH_DAY_YEAR:
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });

      case DateFormat.FULL_DATETIME: {
        const formattedDate = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        const formattedTime = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
        return `${formattedDate} ${formattedTime}`;
      }

      default:
        return dateString;
    }
  } catch {
    return dateString;
  }
}

/**
 * Column formatter for clickable links with text truncation
 *
 * Used in: Pull Requests Opened, Avg Time to Merge PRs
 *
 * @param url - Link URL
 * @param text - Display text (will be truncated if too long)
 * @param maxLength - Maximum length before truncation
 * @returns DataRow cell with clickable link
 */
export function formatClickableLink(
  url: string,
  text: string,
  maxLength = 60
): { text: string; isHtml: boolean; url?: string } {
  if (!url) return { text: text || 'N/A', isHtml: false };

  // If text is empty or missing, use the URL as display text
  const displayText = text && text.trim()
    ? (text.length > maxLength ? `${text.substring(0, maxLength)}...` : text)
    : url;

  const titleAttr = text && text.trim() ? text : url;

  return {
    text: `<a href="${url}" target="_blank" rel="noopener noreferrer" title="${titleAttr}">${displayText}</a>`,
    isHtml: true,
    url
  };
}

/**
 * Column formatter for commit content with link
 *
 * Used in: Commit Activities
 *
 * @param commitContent - Commit message/content
 * @param commitUrl - URL to commit
 * @param maxLength - Maximum length before truncation
 * @returns DataRow cell with commit link
 */
export function formatCommitLink(
  commitContent: string,
  commitUrl: string,
  maxLength = 80
): { text: string; isHtml: boolean; url?: string } {
  if (!commitUrl) return { text: commitContent || 'N/A', isHtml: false };

  const truncatedContent = commitContent && commitContent.length > maxLength
    ? `${commitContent.substring(0, maxLength)}...`
    : commitContent || commitUrl;

  return {
    text: `<a href="${commitUrl}" target="_blank" rel="noopener noreferrer">${truncatedContent}</a>`,
    isHtml: true,
    url: commitUrl
  };
}

/**
 * Summary table column definitions (standard 3-column format)
 *
 * Used in: All metrics for summary data
 *
 * @param secondColumnName - Name for middle column (e.g., "Total count", "Average time")
 * @returns Array of Column definitions
 */
export function createSummaryColumns(
  secondColumnName = 'Total count for this project'
): Column[] {
  return [
    { name: 'Definition', prop: 'definition' },
    { name: secondColumnName, prop: 'totalCount' },
    { name: 'Data source', prop: 'dataSource' }
  ];
}

/**
 * Create summary table data row
 *
 * Used in: All metrics for summary data
 *
 * @param definition - Definition text (can be HTML)
 * @param totalCount - Count or average value
 * @param dataSource - Data source text
 * @param isHtml - Whether definition contains HTML
 * @returns DataRow for summary table
 */
export function createSummaryDataRow(
  definition: string,
  totalCount: string,
  dataSource: string,
  isHtml = false
): DataRow {
  return {
    definition: {
      text: definition,
      isHtml
    },
    totalCount: { text: totalCount },
    dataSource: { text: dataSource }
  };
}

/**
 * Format data sources string with proper capitalization
 *
 * Converts comma-separated platform names to properly capitalized format.
 * Special cases: GitHub, GitLab, Gerrit (platform-specific capitalization)
 *
 * Used in: All metrics for data source display
 *
 * @param dataSources - Comma-separated string of platform names
 * @returns Formatted string with proper capitalization and spacing
 * @example
 * formatDataSources('github,gerrit') // Returns: 'GitHub, Gerrit'
 * formatDataSources('gitlab,github') // Returns: 'GitLab, GitHub'
 */
export function formatDataSources(dataSources: string): string {
  if (!dataSources || dataSources === 'N/A') {
    return 'N/A';
  }
  
  return dataSources
    .split(',')
    .map(source => {
      const trimmed = source.trim();
      const lower = trimmed.toLowerCase();
      
      if (lower === 'github') {
        return 'GitHub';
      } else if (lower === 'gitlab') {
        return 'GitLab';
      } else if (lower === 'gerrit') {
        return 'Gerrit';
      }
      
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    })
    .join(', ');
}

/**
 * Format number with locale-specific thousand separators
 *
 * @param value - Number to format
 * @param locale - Locale string (default: 'en-US')
 * @returns Formatted number string
 */
export function formatNumber(value: number, locale = 'en-US'): string {
  return value.toLocaleString(locale);
}

/**
 * Truncate text to specified length
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text with ellipsis if needed
 */
export function truncateText(text: string, maxLength: number): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Create fallback summary data for error cases
 *
 * @param definition - Fallback definition text
 * @param secondColumnName - Name for middle column
 * @returns Object with columns and data for fallback display
 */
export function createFallbackSummary(
  definition = 'View definition in Insights',
  secondColumnName = 'Total count for this project'
): { columns: Column[]; data: DataRow[] } {
  return {
    columns: createSummaryColumns(secondColumnName),
    data: [createSummaryDataRow(definition, 'N/A', 'N/A', false)]
  };
}

